import React, { useMemo, useState } from 'react';
import { ToastProvider } from '../components/ui/Toast';
import { RecruitingWidget } from '../components/widgets/RecruitingWidget';
import { CandidateModal } from '../components/CandidateModal';
import { SchedulePhoneScreenModal } from '../components/SchedulePhoneScreenModal';
import { ScheduleInterviewModal } from '../components/ScheduleInterviewModal';
import { Candidate } from '../types/hr.types';
import { mockCandidates } from '../utils/mockData';
import { Button } from '../components/ui/Button';
import { Phone, Users, UserCheck } from 'lucide-react';
import { motion } from 'framer-motion';
type RecruitingTab = 'phone-screens' | 'in-person' | 'accepted';
export function Recruiting() {
  const [activeTab, setActiveTab] = useState<RecruitingTab>('phone-screens');
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [schedulingCandidate, setSchedulingCandidate] = useState<Candidate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPhoneScreenModalOpen, setIsPhoneScreenModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>(mockCandidates);
  const filteredCandidates = useMemo(() => {
    if (activeTab === 'phone-screens') return candidates.filter(c => c.status === 'Phone Screen');
    if (activeTab === 'in-person') return candidates.filter(c => c.status === 'In Person Interview');
    return candidates.filter(c => c.status === 'Offer Signed' || c.status === 'Pre-boarding' || c.status === 'Ready to Start');
  }, [candidates, activeTab]);
  const phoneScreenCount = candidates.filter(c => c.status === 'Phone Screen').length;
  const inPersonCount = candidates.filter(c => c.status === 'In Person Interview').length;
  const acceptedCount = candidates.filter(c => c.status === 'Offer Signed' || c.status === 'Pre-boarding' || c.status === 'Ready to Start').length;
  const handleCandidateClick = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedCandidate(null), 300);
  };
  const handleCreateCandidate = (newCandidate: Candidate) => {
    setCandidates(prev => [...prev, newCandidate]);
  };
  const handleUpdateCandidate = (updatedCandidate: Candidate) => {
    setCandidates(prev => prev.map(c => c.id === updatedCandidate.id ? updatedCandidate : c));
    setSelectedCandidate(updatedCandidate);
  };
  const handleOpenSchedule = (candidate: Candidate) => {
    setSchedulingCandidate(candidate);
    setIsScheduleModalOpen(true);
  };
  const handleScheduleConfirm = (date: Date) => {
    if (schedulingCandidate) {
      const updatedCandidate = {
        ...schedulingCandidate,
        status: 'In Person Interview' as const,
        inPersonInterviewDate: date.toISOString()
      };
      handleUpdateCandidate(updatedCandidate);
      setIsScheduleModalOpen(false);
      setSchedulingCandidate(null);
    }
  };
  const tabs = [{
    id: 'phone-screens' as RecruitingTab,
    label: 'Phone Screens',
    icon: Phone,
    count: phoneScreenCount
  }, {
    id: 'in-person' as RecruitingTab,
    label: 'In Person Interviews',
    icon: Users,
    count: inPersonCount
  }, {
    id: 'accepted' as RecruitingTab,
    label: 'Accepted Candidates',
    icon: UserCheck,
    count: acceptedCount
  }];
  return <ToastProvider data-id="element-1373">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50/30 to-gray-50 p-6 md:p-8 font-sans text-gray-900" data-id="element-1374">
        <div className="max-w-[1600px] mx-auto space-y-6" data-id="element-1375">
          <header className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-700 via-orange-600 to-orange-500 p-8 md:p-10 shadow-xl shadow-orange-600/30" data-id="element-1376">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.15)_0%,_transparent_60%)]" data-id="element-1377"></div>
            <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-white/10 to-transparent" data-id="element-1378"></div>
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8" data-id="element-1379">
              <div className="flex items-center gap-8" data-id="element-1380">
                <div className="flex-shrink-0" data-id="element-1381">
                  <img src="https://cdn.magicpatterns.com/uploads/nShniaYpBxVejnwixTrGRA/UML_Logo.png" alt="United Mortgage Lending" className="h-10 md:h-12 w-auto brightness-0 invert" data-id="element-1382" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1383">
                  <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight drop-shadow-sm" data-id="element-1384">
                    Recruiting Pipeline
                  </h1>
                  <p className="text-orange-100 mt-1 text-sm md:text-base" data-id="element-1385">
                    Manage candidates, track interviews, and oversee the hiring
                    process.
                  </p>
                </div>
              </div>
              <div className="relative z-10 self-end md:self-center" data-id="element-1386">
                <Button variant="secondary" onClick={() => setIsPhoneScreenModalOpen(true)} leftIcon={<Phone className="h-4 w-4" data-id="element-1388" />} className="bg-white/90 hover:bg-white border-none shadow-lg" data-id="element-1387">
                  Schedule Phone Screening
                </Button>
              </div>
            </div>
          </header>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-1.5" data-id="element-1389">
            <div className="relative flex" data-id="element-1390">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-semibold rounded-lg transition-colors z-10 ${isActive ? 'text-orange-700' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1391">
                    {isActive && <motion.div layoutId="activeRecruitingTab" className="absolute inset-0 bg-orange-50 rounded-lg border border-orange-100" transition={{
                  type: 'spring',
                  bounce: 0.2,
                  duration: 0.5
                }} data-id="element-1392" />}
                    <span className="relative z-10 flex items-center gap-2" data-id="element-1393">
                      <Icon className="h-4 w-4" data-id="element-1394" />
                      {tab.label}
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${isActive ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-1395">
                        {tab.count}
                      </span>
                    </span>
                  </button>;
            })}
            </div>
          </div>
          <div className="space-y-4" data-id="element-1396">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-1397">
              <div className="h-1 w-8 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full" data-id="element-1398"></div>
              {activeTab === 'phone-screens' ? 'Phone Screen Candidates' : activeTab === 'in-person' ? 'In Person Interview Candidates' : 'Accepted Candidates'}
            </h2>
            <div className="h-full" data-id="element-1399">
              <RecruitingWidget onCandidateClick={handleCandidateClick} candidates={filteredCandidates} data-id="element-1400" />
            </div>
          </div>
        </div>
        <CandidateModal candidate={selectedCandidate} isOpen={isModalOpen} onClose={handleCloseModal} onUpdate={handleUpdateCandidate} onOpenSchedule={handleOpenSchedule} data-id="element-1401" />
        <SchedulePhoneScreenModal isOpen={isPhoneScreenModalOpen} onClose={() => setIsPhoneScreenModalOpen(false)} onCreate={handleCreateCandidate} data-id="element-1402" />
        <ScheduleInterviewModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} candidate={schedulingCandidate} onSchedule={handleScheduleConfirm} data-id="element-1403" />
      </div>
    </ToastProvider>;
}